import { ChevronDown, Sparkles, Plus, TrendingUp, Edit3, Wrench, Undo2, HelpCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { type Intent, INTENT_LABELS } from '@/lib/intentTypes';

export type IntentChoice = 'auto' | Intent;

export interface IntentSelectorProps {
  value: IntentChoice;
  onChange: (choice: IntentChoice) => void;
  disabled?: boolean;
  /** Visar en hint-text vid "Auto" när klassificeraren har valt en intent. */
  autoHint?: { intent: Intent; confidence: number } | null;
}

const ICONS: Record<Intent, React.ComponentType<{ className?: string }>> = {
  build_new: Plus,
  extend: TrendingUp,
  modify_in_place: Edit3,
  refactor: Wrench,
  revert: Undo2,
  explain: HelpCircle,
  'meta.moderation': HelpCircle,
  'meta.history': HelpCircle,
};

const ARTIFACT_INTENTS: Intent[] = [
  'build_new',
  'extend',
  'modify_in_place',
  'refactor',
  'revert',
];
const NON_ARTIFACT_INTENTS: Intent[] = ['explain'];

/**
 * Dropdown som låter user manuellt välja intent för nästa message.
 * Default är 'auto' — klassificeraren styr då.
 *
 * Beställarens svar på fråga 6: manuell override. Content-hint från LLM
 * klassificerare påverkar bara 'auto'-branchen, inte explicit val.
 */
export function IntentSelector({
  value, onChange, disabled, autoHint,
}: IntentSelectorProps) {
  const currentLabel =
    value === 'auto'
      ? autoHint
        ? `Auto → ${INTENT_LABELS[autoHint.intent].sv}`
        : 'Auto'
      : INTENT_LABELS[value].sv;

  const CurrentIcon = value === 'auto' ? Sparkles : ICONS[value];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid="intent-selector-trigger"
          className="inline-flex items-center gap-1.5 text-xs border rounded-md px-2 py-1 hover:bg-accent disabled:opacity-50"
        >
          <CurrentIcon className="w-3.5 h-3.5" />
          <span className="max-w-[140px] truncate">{currentLabel}</span>
          {autoHint && value === 'auto' && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
              {Math.round(autoHint.confidence * 100)}%
            </Badge>
          )}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase">
          Välj intent manuellt
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => onChange('auto')}
          className={value === 'auto' ? 'bg-accent' : ''}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          <div className="flex-1">
            <div className="font-medium">Auto</div>
            <div className="text-[10px] text-muted-foreground">
              Klassificeraren tolkar din prompt
            </div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase">
          Canvas-ändringar
        </DropdownMenuLabel>
        {ARTIFACT_INTENTS.map((intent) => {
          const Icon = ICONS[intent];
          const def = INTENT_LABELS[intent];
          return (
            <DropdownMenuItem
              key={intent}
              onClick={() => onChange(intent)}
              className={value === intent ? 'bg-accent' : ''}
            >
              <Icon className="w-4 h-4 mr-2" />
              <div className="flex-1">
                <div className="font-medium">{def.sv}</div>
                {def.hint && (
                  <div className="text-[10px] text-muted-foreground">{def.hint}</div>
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase">
          Utan canvas-ändring
        </DropdownMenuLabel>
        {NON_ARTIFACT_INTENTS.map((intent) => {
          const Icon = ICONS[intent];
          const def = INTENT_LABELS[intent];
          return (
            <DropdownMenuItem
              key={intent}
              onClick={() => onChange(intent)}
              className={value === intent ? 'bg-accent' : ''}
            >
              <Icon className="w-4 h-4 mr-2" />
              <div className="flex-1">
                <div className="font-medium">{def.sv}</div>
                {def.hint && (
                  <div className="text-[10px] text-muted-foreground">{def.hint}</div>
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
